import { useRef, useState } from "react";
import QRCode from "qrcode";
import { Box, Button, TextField } from "@mui/material";
import { cashiLogo } from "../../include/cashiBrand";
import { consoleColors, headingCase, primaryButtonSx } from "../../pages/admin/consoleTheme";

const PREVIEW = 0.46;
const SWATCHES = ["#21a7ec", "#0a0a0a", "#14324a", "#111827", "#ffffff", "#f87046"];

function inkFor(hex) {
  const value = String(hex || "").replace("#", "");
  if (value.length < 6) return "#ffffff";
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return (red * 299 + green * 587 + blue * 114) / 1000 > 170 ? "#111111" : "#ffffff";
}

function absoluteUrl(src) {
  if (!src) return "";
  try { return new URL(src, window.location.href).href; } catch { return src; }
}

export default function PrintReviewQr({ qrValue, store_name, store_image }) {
  const sheetRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [qrImage, setQrImage] = useState("");
  const [bgColor, setBgColor] = useState("#21a7ec");
  const [textColor, setTextColor] = useState("#ffffff");
  const [headline, setHeadline] = useState("Tell Us What You Think");
  const [message, setMessage] = useState("Your Review Matters");
  const [scanText, setScanText] = useState("Scan To Review");
  const [photoOk, setPhotoOk] = useState(Boolean(store_image));

  const logoSrc = absoluteUrl(cashiLogo);
  const storeName = headingCase(store_name) || "Your Store";

  const openSheet = async () => {
    if (!qrValue) return;
    const canvas = document.createElement("canvas");
    await QRCode.toCanvas(canvas, String(qrValue), { width: 640, margin: 1 });
    setQrImage(canvas.toDataURL("image/png"));
    setPhotoOk(Boolean(store_image));
    setOpen(true);
  };

  const changeBackground = (next) => {
    setBgColor(next);
    setTextColor(inkFor(next));
  };

  const handlePrint = () => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>${storeName} Review QR</title>
      <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@500;650;700;800&display=swap" rel="stylesheet" />
      <style>
        @page { size: A4 portrait; margin: 0; }
        html, body { margin: 0; padding: 0; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      </style></head><body>${sheet.outerHTML}</body></html>`);
    doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 250);
    };
  };

  const sheetStyle = {
    width: "210mm",
    height: "297mm",
    boxSizing: "border-box",
    background: bgColor,
    color: textColor,
    fontFamily: '"Manrope", Helvetica, Arial, sans-serif',
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
    padding: "16mm 16mm 32mm",
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
  };

  return (
    <>
      <Button variant="contained" sx={primaryButtonSx} onClick={openSheet}>Review QR</Button>
      {open && (
        <Box sx={{ position: "fixed", inset: 0, zIndex: 1400, background: "rgba(10,10,10,0.55)", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          <Box sx={{ width: "min(980px, 100%)", maxHeight: "94vh", overflow: "auto", background: "#fff", borderRadius: 3, p: { xs: 2, md: 2.5 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1 }}>
              <Box sx={{ fontSize: 18, fontWeight: 750, color: consoleColors.ink }}>{storeName} : Review QR</Box>
              <Button sx={{ minWidth: 36, color: consoleColors.muted }} onClick={() => setOpen(false)} aria-label="Close">✕</Button>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "auto 280px" }, gap: 2.5, alignItems: "start" }}>
              <Box sx={{ width: `calc(210mm * ${PREVIEW})`, height: `calc(297mm * ${PREVIEW})`, overflow: "hidden", mx: "auto", boxShadow: "0 16px 40px rgba(15,23,42,0.16)", borderRadius: 1 }}>
                <Box sx={{ transform: `scale(${PREVIEW})`, transformOrigin: "top left" }}>
                  <div ref={sheetRef} style={sheetStyle}>
                    <div style={{ position: "absolute", width: "92mm", height: "92mm", borderRadius: "50%", background: "rgba(255,255,255,0.10)", top: "-24mm", right: "-28mm" }} />
                    <div style={{ position: "absolute", width: "74mm", height: "74mm", borderRadius: "50%", background: "rgba(0,0,0,0.08)", bottom: "18mm", left: "-30mm" }} />
                    <div style={{ position: "relative", zIndex: 1, flex: "1 1 auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%" }}>
                      {store_image && photoOk && (
                        <img src={store_image} alt="" onError={() => setPhotoOk(false)} style={{ width: "28mm", height: "28mm", objectFit: "cover", borderRadius: "50%", border: "3px solid rgba(255,255,255,0.85)", marginBottom: "6mm" }} />
                      )}
                      <div style={{ fontSize: "11px", letterSpacing: "3px", fontWeight: 750, opacity: 0.85 }}>LEAVE A REVIEW</div>
                      <div style={{ fontSize: "34px", fontWeight: 800, textAlign: "center", lineHeight: 1.15, marginTop: "4mm", maxWidth: "160mm" }}>{storeName}</div>
                      <div style={{ marginTop: "5mm", letterSpacing: "4px", fontSize: "18px", color: "#f5c542" }}>★★★★★</div>
                      <div style={{ fontSize: "26px", fontWeight: 750, textAlign: "center", lineHeight: 1.25, marginTop: "6mm", maxWidth: "150mm" }}>{headline}</div>
                      <div style={{ fontSize: "16px", textAlign: "center", marginTop: "3mm", opacity: 0.9, maxWidth: "140mm" }}>{message}</div>
                      <div style={{ marginTop: "10mm", background: "#fff", borderRadius: "8mm", padding: "7mm", boxShadow: "0 12px 30px rgba(0,0,0,0.18)" }}>
                        {qrImage && <img src={qrImage} alt="Review QR" style={{ width: "68mm", height: "68mm", display: "block" }} />}
                      </div>
                      <div style={{ marginTop: "8mm", border: `2px solid ${textColor}`, borderRadius: "999px", padding: "3.5mm 10mm", fontSize: "18px", fontWeight: 800, letterSpacing: "0.4px" }}>{scanText}</div>
                    </div>
                    <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "26mm", background: "#06568b", color: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", fontSize: "30px", fontWeight: 700 }}>
                      Powered by
                      <img src={logoSrc} alt="Cashi" style={{ height: "14mm", width: "auto" }} />
                    </div>
                  </div>
                </Box>
              </Box>

              <Box sx={{ display: "grid", gap: 1.4, alignContent: "start" }}>
                <Box sx={{ fontSize: 13, color: consoleColors.muted }}>Prints as one A4 page. Change the colour or the words, then print.</Box>
                <Box>
                  <Box sx={{ fontSize: 13, fontWeight: 700, mb: 0.6 }}>Background</Box>
                  <Box sx={{ display: "flex", gap: 0.8, alignItems: "center", flexWrap: "wrap" }}>
                    {SWATCHES.map((swatch) => (
                      <Box key={swatch} component="button" type="button" aria-label={swatch} onClick={() => changeBackground(swatch)} sx={{ width: 28, height: 28, borderRadius: "50%", border: bgColor === swatch ? `2px solid ${consoleColors.ink}` : `1px solid ${consoleColors.line}`, background: swatch, cursor: "pointer", p: 0 }} />
                    ))}
                    <input type="color" value={bgColor} aria-label="Background colour" onChange={(event) => changeBackground(event.target.value)} style={{ width: 36, height: 32, border: 0, background: "transparent", cursor: "pointer" }} />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ fontSize: 13, fontWeight: 700 }}>Text</Box>
                  <input type="color" value={textColor} aria-label="Text colour" onChange={(event) => setTextColor(event.target.value)} style={{ width: 36, height: 32, border: 0, background: "transparent", cursor: "pointer" }} />
                </Box>
                <TextField size="small" label="Headline" value={headline} onChange={(event) => setHeadline(event.target.value)} />
                <TextField size="small" label="Message" value={message} onChange={(event) => setMessage(event.target.value)} />
                <TextField size="small" label="Scan text" value={scanText} onChange={(event) => setScanText(event.target.value)} />
                <Button variant="contained" sx={primaryButtonSx} onClick={handlePrint}>Print A4</Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
